import React, { useState, useCallback } from "react";
import FaleAPI from "fale";
import { useDropzone } from "react-dropzone";
import _ from "lodash";
import { FAQs } from "../../helpers/constants";

function Onboard() {
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  const [stepIndex, setStepIndex] = useState(0);

  const setFormValue = (prop, value) => {
    setValues({ ...values, [prop]: value });
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];

      setLoading(true);

      const data = await FaleAPI.files.upload(file, "identification");

      setValues({ ...values, image: data.key, file: file });
      setLoading(false);
    },
    [values]
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const createStep = (
    title,
    name,
    label,
    prop,
    placeholder,
    component,
    type,
    required
  ) => ({
    prop,
    required,
    section: title,
    name,
    component: (next) => (
      <>
        <br />
        <br />
        <h4>{title}</h4>
        <br />
        <div className="field-wrapper">
          <p>{label}</p>
          {component ? (
            component()
          ) : (
            <input
              ref={(input) => input && input.focus()}
              style={{ color: "black" }}
              type={type || "text"}
              autoFocus={true}
              value={values[prop] || ""}
              onChange={(event) => setFormValue(prop, event.target.value)}
              placeholder={placeholder}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  next();
                }
              }}
            />
          )}
        </div>
        {required ? <p className="text-danger">** {required}</p> : null}
        <br />
        <br />
      </>
    ),
    previous: { label: "Back" },
    next: { label: "Next" },
  });

  const sections = [
    {
      name: "Kitchen details",
      fields: [
        {
          name: "Kitchen name",
          label: (
            <>
              What is the name of your kitchen?
              <br />
              This will be visible to all customers in the Fale Marketplace App.
            </>
          ),
          prop: "kitchenName",
          placeholder: "Jim's BBQ House",
          required:
            "A kitchen name is required for joining Fale as an approved kitchen.",
        },
        {
          name: "NZBN",
          label: (
            <>
              What is your <strong>NZBN</strong>? (New Zealand Business Number){" "}
              <br /> Ignore this step if you don't have one.
            </>
          ),
          prop: "nzbn",
          placeholder: "Unique 13-digit business identifier",
        },
      ],
    },
    {
      name: "Personal details",
      fields: [
        {
          name: "First name",
          label: (
            <>
              What is your <strong>legal first name</strong>?<br />
              This will need to be consistent with your drivers license or
              passport.
            </>
          ),
          prop: "firstName",
          placeholder: "Sione",
          required: "Your first name is required",
        },
        {
          name: "Last name",
          label: (
            <>
              What is your <strong>legal last name</strong>?<br />
              This will need to be consistent with your drivers license or
              passport.
            </>
          ),
          prop: "lastName",
          placeholder: "Finau",
          required: "Your last name is required",
        },
        {
          name: "Email",
          label: (
            <>
              What is your <strong>email address</strong>? <br /> This email
              address needs to be active as it will be used to verify your
              account.
            </>
          ),
          prop: "email",
          placeholder: "email@gmail.com",
          required: "Your email is required",
        },
        {
          name: "Date of birth",
          label: (
            <>
              What is your <strong>date of birth</strong>?
            </>
          ),
          prop: "dateOfBirth",
          placeholder: "08/02/1993",
          required: "Your date of birth is required",
        },
        {
          name: "Home address",
          label: (
            <>
              What is your <strong>home address</strong>?
            </>
          ),
          prop: "homeAddress",
          placeholder: "18 Wickman Way, Mangere East, Auckland",
          required: "Your address is required",
        },
        {
          name: "Phone number",
          label: (
            <>
              What is your <strong>phone number</strong>?
            </>
          ),
          prop: "phone",
          placeholder: "021 123 4567",
          required: "Your phone number is required",
        },
      ],
    },
    {
      name: "Bank details",
      fields: [
        {
          name: "Bank account number",
          label: (
            <>
              What is your <strong>bank account number</strong>? <br /> We will
              send payouts to this bank account when the transactions have been
              processed.
            </>
          ),
          prop: "bankAccountNumber",
          placeholder: "00-0000-0000000-000",
          required:
            "Your bank account number is required for receiving payouts through Fale",
        },
      ],
    },
    {
      name: "Identity verification",
      fields: [
        {
          name: "Upload proof",
          label: (
            <>
              Please upload an image of your drivers license or passport for
              account verification purposes, make sure it displays your first
              and last name in the image.
            </>
          ),
          prop: "image",
          component: () => (
            <section className="container">
              <br />
              <div>
                <p className="text-success mb-2">
                  {values.file
                    ? `${values.file?.name} has been uploaded successfully!`
                    : null}
                </p>
                <div
                  {...getRootProps({
                    className: "dropzone",
                  })}
                >
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p style={{ padding: "20px" }}>Drop the file here ...</p>
                  ) : (
                    <p style={{ padding: "20px" }}>
                      Drag 'n' drop a photo of your drivers license or passport
                      here
                    </p>
                  )}
                </div>
              </div>
            </section>
          ),
          required: "Proof of your identity is required",
        },
      ],
    },
    {
      name: "Portal access",
      fields: [
        {
          name: "Password",
          label: (
            <>
              Please type in a strong password for your Fale portal access.
              <br />
              In order to login to the platform, you'll need your email and
              password.
            </>
          ),
          prop: "password",
          placeholder: "",
          type: "password",
          required:
            "A password is required for accessing the Fale Portal for Kitchens",
        },
      ],
    },
  ];

  const steps = [
    {
      section: "Welcome",
      name: "Welcome",
      component: () => (
        <>
          <br />
          <br />
          <h4>Onboarding for Kitchens</h4>
          <br />
          <p>Welcome to the Fale Kitchen Portal,</p>
          <br />
          <p>
            Our goal is to bring back home cooked meals, 1 kitchen at a time!
          </p>
          <br />
          <p>
            In order to get started, please go through this onboarding process
            and provide us with as much detail as possible.
          </p>
          <br />
          <p>
            If you have any issues answering these questions, please let Tino
            know through Messenger or contact him via email:{" "}
            <a href="mailto:tino@fale.co.nz">tino@fale.co.nz</a>
          </p>
          <br />
        </>
      ),
      next: { label: "Show me the FAQs" },
    },
    {
      section: "FAQs",
      name: "FAQs",
      component: () => (
        <>
          <br />
          <br />
          <h4>Frequently Asked Questions</h4>
          <br />
          <p>
            Here are some questions we think are important to answer up front:
          </p>
          <br />
          <div className="accordion accordion-flush mt-2" id="accordion">
            {FAQs().map((question, index) => (
              <div key={`question${index}`} className="accordion-item">
                <h2 className="accordion-header" id={`heading${index}`}>
                  <button
                    style={{
                      fontSize: "12px",
                      paddingTop: "10px",
                      paddingBottom: "10px",
                    }}
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse${index}`}
                    aria-expanded="false"
                    aria-controls={`collapse${index}`}
                  >
                    {question.question}
                  </button>
                </h2>
                <div
                  id={`collapse${index}`}
                  className="accordion-collapse collapse"
                  aria-labelledby={`heading${index}`}
                  data-bs-parent="#accordion"
                >
                  <div className="accordion-body">{question.answer}</div>
                </div>
              </div>
            ))}
          </div>
          <br />
        </>
      ),
      previous: { label: "Back" },
      next: { label: "Get Started" },
    },
    ..._.flatten(
      sections
        .filter((s) => !s.hide)
        .map((section) =>
          section.fields.map((field) =>
            createStep(
              section.name,
              field.name,
              field.label,
              field.prop,
              field.placeholder,
              field.component,
              field.type,
              field.required
            )
          )
        )
    ),
    {
      section: "Review details",
      name: "Review details",
      component: () => (
        <>
          <br />
          <br />
          <h4>Review</h4>
          <br />
          <p>Please review these details carefully before you proceed.</p>
          <br />
          <p>
            <strong>Kitchen name: </strong> {values.kitchenName}
          </p>
          <p>
            <strong>NZBN: </strong>{" "}
            {values.nzbn ? values.nzbn : "I don't have one"}
          </p>
          <p>
            <strong>First name: </strong> {values.firstName}
          </p>
          <p>
            <strong>Last name: </strong> {values.lastName}
          </p>
          <p>
            <strong>Email: </strong> {values.email}
          </p>
          <p>
            <strong>Date of birth: </strong> {values.dateOfBirth}
          </p>
          <p>
            <strong>Home address: </strong> {values.homeAddress}
          </p>
          <p>
            <strong>Phone number: </strong> {values.phone}
          </p>
          <p>
            <strong>Bank account number: </strong> {values.bankAccountNumber}
          </p>

          <br />
        </>
      ),
      previous: { label: "Back" },
      next: { label: "Looks good" },
    },
    {
      section: "Terms and Conditions",
      name: "Terms",
      component: () => (
        <>
          <br />
          <br />
          <h4>Terms and Conditions</h4>
          <br />
          <p>
            Please read the terms and conditions for kitchens carefully and only
            click "I accept" if you agree to these terms.
          </p>
          <br />
          <p>
            <strong>
              <a
                style={{ color: "#e02539" }}
                href="https://fale.co.nz/kitchen-terms"
                target="_blank"
                rel="noreferrer"
              >
                Click here for the full terms and conditions for kitchens
              </a>
            </strong>
          </p>
          <br />

          <br />
          <p>
            If you have any questions or concerns, please contact Tino through
            Messenger or email:{" "}
            <a href="mailto:tino@fale.co.nz">tino@fale.co.nz</a>
          </p>
          <br />
        </>
      ),
      previous: { label: "Back" },
      next: {
        label: "I accept",
        onClick: async () => {
          setLoading(true);

          FaleAPI.authentication.signup(values).then((user) => {
            setLoading(false);

            localStorage.setItem("sessionToken", user.hash);

            window.location.href = "/welcome";
          });
        },
      },
    },
  ];

  const currentStep = steps[stepIndex];

  const renderStep = (section) =>
    currentStep.section === section ? (
      <li style={{ paddingTop: "10px" }}>
        <strong>{section}</strong>
      </li>
    ) : (
      <li style={{ paddingTop: "10px" }}>{section}</li>
    );

  const nextDisabled = currentStep.required && !values[currentStep.prop];
  const next = nextDisabled
    ? () => {}
    : currentStep.next.onClick
    ? currentStep.next.onClick
    : () => {
        setStepIndex(stepIndex + 1);
      };

  return (
    <div className="main-container" style={{ paddingLeft: "0px" }}>
      <div className="content-wrapper-scroll">
        <div className="content-wrapper">
          <div className="login-container">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="login-wrapper">
                    <div className="login-screen" style={{ marginTop: "40px" }}>
                      <div className="login-body">
                        <div className="row">
                          <img
                            style={{ width: "70px" }}
                            src="/assets/img/logo.png"
                            alt="Uni Pro Admin"
                          />
                        </div>

                        <div className="row">
                          <div className="col-4 d-none d-sm-block d-xs-block d-md-block">
                            <br />
                            <br />
                            <ol
                              style={{
                                listStyleType: "decimal",
                                paddingTop: "10px",
                                paddingBottom: "30px",
                              }}
                            >
                              {renderStep("Welcome")}
                              {renderStep("FAQs")}

                              {sections
                                .filter((s) => !s.hide)
                                .map((section) => (
                                  <li
                                    key={section.name}
                                    style={{ paddingTop: "10px" }}
                                  >
                                    {currentStep.section === section.name ? (
                                      <strong>{section.name}</strong>
                                    ) : (
                                      section.name
                                    )}
                                    <ul
                                      style={{
                                        listStyleType: "circle",
                                        paddingLeft: "20px",
                                      }}
                                    >
                                      {section.fields.map((field) => (
                                        <li key={field.name}>
                                          {currentStep.name === field.name ? (
                                            <strong>{field.name}</strong>
                                          ) : (
                                            field.name
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </li>
                                ))}

                              {renderStep("Review details")}
                              {renderStep("Terms and Conditions")}
                            </ol>
                          </div>
                          <div className="col-md-8 col-sm-8 col-xs-12">
                            {currentStep.component(next)}
                          </div>
                        </div>

                        <div className="actions">
                          {currentStep.previous && (
                            <button
                              className="btn btn-primary"
                              onClick={() => {
                                setStepIndex(stepIndex - 1);
                              }}
                            >
                              {currentStep.previous?.label}
                            </button>
                          )}
                          <button
                            disabled={nextDisabled}
                            className="btn btn-primary ms-auto"
                            onClick={next}
                          >
                            {currentStep.next.label}{" "}
                          </button>
                          {loading ? (
                            <div
                              className="spinner-border text-light ms-4"
                              role="status"
                            ></div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Onboard;
